import React from 'react';
import { 
  BookOpen, Shield, Search, CheckCircle, Globe, Building2, 
  FileText, Brain, Zap, ArrowRight, ExternalLink
} from 'lucide-react';

const EvidenceBasedSection = ({ isDark }) => {
  const guidelines = [
    { name: 'NICE', fullName: 'National Institute for Health and Care Excellence', region: 'UK', color: 'blue' },
    { name: 'WHO', fullName: 'World Health Organization', region: 'Global', color: 'emerald' },
    { name: 'ESC', fullName: 'European Society of Cardiology', region: 'Europe', color: 'purple' },
    { name: 'ADA', fullName: 'American Diabetes Association', region: 'USA', color: 'amber' },
    { name: 'CDC', fullName: 'Centers for Disease Control', region: 'USA', color: 'rose' },
    { name: 'GOLD', fullName: 'Global Initiative for COPD', region: 'Global', color: 'cyan' }
  ];

  const features = [
    {
      icon: Brain,
      title: 'Clinical Intelligence Engine',
      description: 'Real-time differential diagnosis with probability scoring based on patient context, symptoms, and clinical findings.',
      highlight: 'Multi-factor reasoning'
    },
    {
      icon: BookOpen,
      title: 'Guideline Retrieval',
      description: 'Instant access to relevant clinical guidelines during consultation. Evidence appears contextually when needed.',
      highlight: 'Context-aware'
    },
    {
      icon: Building2,
      title: 'Local Protocol Integration',
      description: 'Your hospital protocols take priority. SkyDoc adapts to your antibiotic policies, admission criteria, and care pathways.',
      highlight: 'Hospital-specific'
    },
    {
      icon: Shield,
      title: 'Red Flag Detection',
      description: 'Automatic identification of warning signs that require immediate attention. Never miss a critical symptom.',
      highlight: 'Safety first'
    }
  ];

  return (
    <section 
      id="evidence" 
      className={`section-padding ${isDark ? 'bg-slate-900' : 'bg-white'}`}
      data-testid="evidence-based-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isDark ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-50 text-purple-600'
          }`}>
            Evidence-Based Practice
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            Trusted Clinical Intelligence at Every Step
          </h2>
          <p className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            SkyDoc brings evidence-based medicine to the point of care. Every recommendation is grounded in 
            trusted guidelines, local protocols, and real-time clinical context.
          </p>
        </div>

        {/* Guidelines Grid */}
        <div className="mb-16">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Globe className={`w-5 h-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <p className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Integrated Clinical Guidelines</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {guidelines.map((guide) => (
              <div 
                key={guide.name}
                className={`rounded-xl p-4 text-center hover:shadow-md transition-shadow cursor-pointer border ${
                  isDark 
                    ? 'bg-slate-800 border-slate-700 hover:border-blue-600' 
                    : 'bg-slate-50 border-slate-200 hover:border-blue-200'
                }`}
                data-testid={`guideline-${guide.name.toLowerCase()}`}
              >
                <p className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>{guide.name}</p>
                <p className={`text-xs line-clamp-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{guide.fullName}</p>
                <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${
                  isDark ? 'bg-slate-700 text-slate-300' : 'bg-white text-slate-400'
                }`}>
                  {guide.region}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`rounded-2xl p-6 lg:p-8 border transition-colors ${
                isDark 
                  ? 'bg-slate-800 border-slate-700 hover:border-purple-600' 
                  : 'bg-slate-50 border-slate-200 hover:border-purple-200'
              }`}
              data-testid={`evidence-feature-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isDark ? 'bg-purple-900/50' : 'bg-purple-100'
                }`}>
                  <feature.icon className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{feature.title}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {feature.highlight}
                    </span>
                  </div>
                  <p className={isDark ? 'text-slate-300' : 'text-slate-600'}>{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                How Evidence Support Works
              </h3>
              <p className="text-slate-300 mb-6">
                SkyDoc's Clinical Copilot doesn't just transcribe — it thinks alongside you, 
                surfacing the right evidence at the right moment.
              </p>
              
              <div className="space-y-4">
                <EvidenceStep 
                  number={1}
                  title="Context Understanding"
                  description="AI captures symptoms, history, and clinical context in real-time"
                />
                <EvidenceStep 
                  number={2}
                  title="Knowledge Retrieval"
                  description="Relevant guidelines and protocols are retrieved automatically"
                />
                <EvidenceStep 
                  number={3}
                  title="Clinical Reasoning"
                  description="Multi-factor analysis generates differential diagnoses with probabilities"
                />
                <EvidenceStep 
                  number={4}
                  title="Actionable Support"
                  description="Evidence-based recommendations appear contextually during consultation"
                />
              </div>
            </div>

            {/* Mock Evidence UI */}
            <div className="bg-white rounded-xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Brain className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="font-semibold text-slate-800">Clinical Copilot</span>
                </div>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                  Active
                </span>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Search className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Guideline Match</span>
                  </div>
                  <p className="text-sm text-blue-700">NICE CG191: Assessment of chest pain</p>
                  <div className="flex items-center gap-1 mt-2">
                    <ExternalLink className="w-3 h-3 text-blue-500" />
                    <span className="text-xs text-blue-500">View full guideline</span>
                  </div>
                </div>

                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Red Flag Check</span>
                  </div>
                  <p className="text-sm text-amber-700">Rule out: Acute coronary syndrome, PE, Aortic dissection</p>
                </div>

                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">Suggested Action</span>
                  </div>
                  <p className="text-sm text-purple-700">Order: ECG, Troponin, Chest X-ray</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                  Evidence confidence: High • Source: NICE 2023
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className={`mb-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            See how evidence-based intelligence supports clinical decision-making
          </p>
          <button 
            className={`inline-flex items-center gap-2 font-medium hover:gap-3 transition-all ${
              isDark ? 'text-purple-400' : 'text-purple-600'
            }`}
            data-testid="evidence-learn-more-btn"
          >
            Explore Clinical Copilot
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

const EvidenceStep = ({ number, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
      <span className="text-white text-sm font-bold">{number}</span>
    </div>
    <div>
      <p className="font-semibold text-white">{title}</p>
      <p className="text-sm text-slate-400">{description}</p>
    </div>
  </div>
);

export default EvidenceBasedSection;
